// Installation de l'application terrain (PWA, D-040) : enregistrement du service worker produit
// par vite-plugin-pwa (sw.js à la racine, en construction seulement), mise à jour signalée,
// proposition d'installation quand le navigateur l'offre, mode installé détecté.

export interface EtatPwa {
  supporte: boolean;
  enregistre: boolean;
  pretHorsConnexion: boolean;
  miseAJourDisponible: boolean;
  installable: boolean;
  installee: boolean;
  erreur: string | null;
}

export const ETAT_PWA_INITIAL: EtatPwa = { supporte: false, enregistre: false, pretHorsConnexion: false, miseAJourDisponible: false, installable: false, installee: false, erreur: null };

interface EvenementInstallation extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let evenementInstallation: EvenementInstallation | null = null;

export function serviceWorkerSupporte(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

export function modeInstalle(): boolean {
  if (typeof window === 'undefined') return false;
  const autonome = typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
  return autonome || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

export const MESSAGE_PREPARATION_INTERROMPUE = 'Préparation hors connexion interrompue par la coupure du réseau : elle reprendra au retour du réseau';

/**
 * Enregistre sw.js (version construite seulement : en développement, Vite ne le produit pas) et
 * suit son cycle : prêt hors connexion seulement quand un worker actif contrôle la page (la mise en
 * cache de la coquille est alors complète), mise à jour quand un nouveau worker est installé
 * derrière un worker actif. Une première installation interrompue par une coupure (mise en cache
 * incomplète, worker abandonné) est signalée, puis relancée au retour du réseau (D-137).
 */
export async function enregistrerServiceWorker(surEtat: (e: Partial<EtatPwa>) => void, actif = import.meta.env.PROD): Promise<void> {
  if (!serviceWorkerSupporte()) {
    surEtat({ supporte: false });
    return;
  }
  surEtat({ supporte: true, installee: modeInstalle() });
  if (!actif) return;
  const conteneur = navigator.serviceWorker;
  const suivre = (w: ServiceWorker | null) => {
    w?.addEventListener('statechange', () => {
      if (w.state === 'installed' && conteneur.controller) surEtat({ miseAJourDisponible: true });
      if (w.state === 'activated') surEtat({ pretHorsConnexion: true, erreur: null });
      if (w.state === 'redundant' && !conteneur.controller) surEtat({ pretHorsConnexion: false, erreur: MESSAGE_PREPARATION_INTERROMPUE });
    });
  };
  const enregistrer = async () => {
    const enregistrement = await conteneur.register('/sw.js', { scope: '/' });
    surEtat({ enregistre: true, pretHorsConnexion: Boolean(conteneur.controller) });
    // Première installation : l'événement updatefound a pu précéder la résolution de register().
    suivre(enregistrement.installing);
    enregistrement.addEventListener('updatefound', () => suivre(enregistrement.installing));
    return enregistrement;
  };
  try {
    conteneur.addEventListener('controllerchange', () => surEtat({ pretHorsConnexion: true, erreur: null }));
    const enregistrement = await enregistrer();
    window.addEventListener('online', () => {
      if (!conteneur.controller) void enregistrer().catch(() => undefined);
    });
    void enregistrement.update().catch(() => undefined);
  } catch (e) {
    surEtat({ erreur: e instanceof Error ? e.message : 'Enregistrement du service worker impossible' });
  }
}

/** Capture la proposition d'installation du navigateur ; renvoie la fonction de désabonnement. */
export function ecouterInstallation(surEtat: (e: Partial<EtatPwa>) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const avant = (e: Event) => {
    e.preventDefault();
    evenementInstallation = e as EvenementInstallation;
    surEtat({ installable: true });
  };
  const apres = () => {
    evenementInstallation = null;
    surEtat({ installable: false, installee: true });
  };
  window.addEventListener('beforeinstallprompt', avant);
  window.addEventListener('appinstalled', apres);
  return () => {
    window.removeEventListener('beforeinstallprompt', avant);
    window.removeEventListener('appinstalled', apres);
  };
}

export async function proposerInstallation(): Promise<'acceptee' | 'refusee' | 'indisponible'> {
  const e = evenementInstallation;
  if (!e) return 'indisponible';
  await e.prompt();
  const choix = await e.userChoice;
  if (choix.outcome === 'accepted') evenementInstallation = null;
  return choix.outcome === 'accepted' ? 'acceptee' : 'refusee';
}
